// Offline prefetch, two tiers:
//
// 1) CALENDAR IDENTITY WINDOW — today −7d .. +35d of /calendar-appointments
//    (org-scoped server-side), fetched in weekly chunks and stored as the
//    six-key feed (appointments + patient identities + cases + referring
//    providers + users + appointment types). This is what the front desk
//    needs: the schedule, check-in, and patient lookup.
//    Refreshed about hourly with an `updated_after` delta. Offline mode
//    exists to prevent total downtime, not to be minutes-fresh: the user's
//    own actions and realtime-ingested changes already land in the cache.
//
// 2) CHART DEPTH — full chart data (patient, cases, POCs, notes, encounters,
//    forms, reports, flow sheet entries) only for the signed-in user's OWN
//    patients with appointments today −1 .. +3 business days, capped. Users
//    who treat nobody (front desk, billing) fetch no charts at all. Chart
//    depth is 8 requests per patient, so each patient is refetched at most
//    once per CHART_FRESH_MS and a reload or second tab costs nothing.
//
// Load shaping: the run starts after a random delay, every request goes out
// at LOW queue priority so user clicks always jump ahead, charts are fetched
// one patient at a time, and nothing is fetched because a client reconnects.

import { cacheWriter } from './cacheWriter';
import { connectivityMonitor } from './connectivityMonitor';
import { isOfflineModeEnabled, onOfflineModeDisabled } from './featureFlag';
import { PRIORITY } from '../api/client';
import offlineDb from './db';

const ORG_STALE_MS = 2 * 60 * 60 * 1000; // org-level data refresh
const WINDOW_PAST_DAYS = 7;
const WINDOW_FUTURE_DAYS = 35;
const WINDOW_REFILL_MS = 12 * 60 * 60 * 1000;
const TICK_INTERVAL_MS = [50 * 60 * 1000, 70 * 60 * 1000];
// A refresh younger than this (by any tab: the stamp lives in IndexedDB) is
// fresh enough. Each call returns the full 43-day related lists whatever the
// gap, so load is simply open tabs divided by this interval.
const REFRESH_MIN_AGE_MS = 50 * 60 * 1000;
const CHART_PAST_DAYS = 1;
const CHART_FUTURE_BUSINESS_DAYS = 3;
const MAX_CHART_PATIENTS = 60;
const MAX_CONCURRENT_PATIENTS = 1;
const CHART_FRESH_MS = 12 * 60 * 60 * 1000;
const START_DELAY_MS = [15 * 1000, 60 * 1000];
const CHART_STATUSES = ['scheduled', 'confirmed', 'checked_in', 'in_progress', 'completed'];

function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function randomBetween([min, max]) {
  return min + Math.floor(Math.random() * (max - min));
}

// Same methods as the api service, but every request is queued at LOW
// priority. Domain methods call this.request, so the override applies to
// all of them; the call still runs through the audit and offline layers.
function withLowPriority(apiService) {
  const proxy = Object.create(apiService);
  proxy.request = (endpoint, options = {}, methodName = null, queueOptions = {}) =>
    apiService.request(endpoint, options, methodName, { ...queueOptions, priority: PRIORITY.LOW });
  return proxy;
}

function addBusinessDays(d, n) {
  const out = new Date(d);
  let remaining = n;
  while (remaining > 0) {
    out.setDate(out.getDate() + 1);
    const day = out.getDay();
    if (day !== 0 && day !== 6) remaining--;
  }
  return out;
}

class PrefetchService {
  constructor() {
    this._abortController = null;
    this._running = false;
    this._incrementalTimer = null;
    this._userId = null;
    this._organizationId = null;
    this._apiService = null;
  }

  get isRunning() {
    return this._running;
  }

  abort() {
    // Keep the controller: the running loop checks its signal, and the run's
    // own finally block releases it. (Nulling it here made _aborted() read
    // false, so an aborted run kept going.)
    if (this._abortController) {
      this._abortController.abort();
    }
    if (this._incrementalTimer) {
      clearTimeout(this._incrementalTimer);
      this._incrementalTimer = null;
    }
    this._running = false;
  }

  _aborted() {
    return Boolean(this._abortController?.signal.aborted) || !isOfflineModeEnabled();
  }

  // Entry point — signature unchanged from v1 (called from App.jsx on login).
  async prefetchForProvider(providerId, organizationId, userId, apiService) {
    if (this._running) return;
    if (!connectivityMonitor.isOnline()) return;

    apiService = withLowPriority(apiService);
    this._userId = userId;
    this._organizationId = organizationId;
    this._apiService = apiService;
    this._running = true;
    this._abortController = new AbortController();

    try {
      // Let the dashboard load first, and spread a login wave out.
      await new Promise(resolve => setTimeout(resolve, randomBetween(START_DELAY_MS)));
      if (this._aborted() || !isOfflineModeEnabled() || !connectivityMonitor.isOnline()) return;

      await this._updateState(providerId, 'in_progress', 0, 0);

      await this._prefetchOrgData(organizationId, userId, apiService);
      if (this._aborted()) return;

      await this.fillCalendarWindow();
      if (this._aborted()) return;

      await this._prefetchChartDepth(providerId, apiService);

      await this._updateState(providerId, 'complete', 0, 0);
      this._startIncrementalRefresh();
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[PrefetchService] Prefetch failed:', e);
        await this._updateState(providerId, 'error', 0, 0);
      }
    } finally {
      this._running = false;
      this._abortController = null;
    }
  }

  // -------------------------------------------------------------------------
  // Calendar identity window
  // -------------------------------------------------------------------------

  async fillCalendarWindow() {
    if (!isOfflineModeEnabled()) return;
    const now = new Date();
    const start = toDateStr(addDays(now, -WINDOW_PAST_DAYS));
    const end = toDateStr(addDays(now, WINDOW_FUTURE_DAYS));

    const meta = await cacheWriter.getCacheMeta('calendarWindow');
    if (meta && meta.start === start && meta.end === end &&
        (Date.now() - (meta.filledAt || 0)) < WINDOW_REFILL_MS) {
      return;
    }

    // Rolling top-up: window already filled recently, only the end moved.
    let chunkStart = new Date(now);
    chunkStart.setDate(chunkStart.getDate() - WINDOW_PAST_DAYS);
    if (meta && meta.end && meta.end < end && meta.start <= start &&
        (Date.now() - (meta.filledAt || 0)) < WINDOW_REFILL_MS) {
      chunkStart = addDays(new Date(`${meta.end}T12:00:00`), 1);
    }

    const windowEnd = new Date(`${end}T12:00:00`);
    while (chunkStart <= windowEnd) {
      if (this._aborted()) return;
      const chunkEnd = addDays(chunkStart, 6) > windowEnd ? windowEnd : addDays(chunkStart, 6);
      await this._fetchCalendarChunk(toDateStr(chunkStart), toDateStr(chunkEnd));
      chunkStart = addDays(chunkEnd, 1);
    }

    // Prune identities/appointments that rolled out of the window.
    await this._pruneBeforeWindow(start);

    await offlineDb.cacheMeta.put({
      key: 'calendarWindow',
      start,
      end,
      filledAt: Date.now(),
      lastIncrementalAt: Date.now(),
      lastFetched: Date.now(),
    });
  }

  async _fetchCalendarChunk(startDate, endDate) {
    try {
      // getCalendarAppointments returns the STITCHED array; the raw six-key
      // feed is captured by the middleware's cacheFromEndpoint pass-through
      // write, so simply issuing the request populates the cache.
      await this._apiService.getCalendarAppointments({ start_date: startDate, end_date: endDate });
      // Reconcile: drop cached rows in this range the server no longer
      // returns (soft-deleted), unless offline-modified.
      await this._reconcileRange(startDate, endDate);
    } catch (e) {
      if (e.name === 'AbortError') throw e;
      console.warn(`[PrefetchService] calendar chunk ${startDate}..${endDate} failed:`, e.message);
    }
  }

  async _reconcileRange(startDate, endDate) {
    const fetchedAt = Date.now();
    // Rows in range whose _cachedAt is older than this fetch and that were
    // not locally modified must have been dropped by the server.
    const stale = await offlineDb.appointments
      .where('appointment_date')
      .between(startDate, endDate, true, true)
      .filter(a => a._cachedAt && a._cachedAt < fetchedAt - 60000 && !a._localTouchedAt)
      .primaryKeys();
    if (stale.length) {
      await offlineDb.appointments.bulkDelete(stale);
    }
  }

  async _pruneBeforeWindow(startDate) {
    try {
      const old = await offlineDb.appointments
        .where('appointment_date')
        .below(startDate)
        .filter(a => a._cachedAt && !a._localTouchedAt)
        .primaryKeys();
      if (old.length) await offlineDb.appointments.bulkDelete(old);
    } catch {
      // non-critical
    }
  }

  // Hourly tick (jittered per client). There is deliberately NO refresh on
  // reconnect: nothing can change server-side while the backend is down, and
  // recovery is the worst moment to add load. Tab focus runs the same tick,
  // which the shared staleness stamp turns into a no-op unless one is due; it
  // is also what gives a tab left open overnight its morning window fill.
  _startIncrementalRefresh() {
    if (!this._focusListening) {
      this._focusListening = true;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this._tick();
      });
    }
    if (this._incrementalTimer) return;
    const schedule = () => {
      this._incrementalTimer = setTimeout(async () => {
        await this._tick();
        if (this._incrementalTimer) schedule();
      }, randomBetween(TICK_INTERVAL_MS));
    };
    schedule();
  }

  async _tick() {
    if (!isOfflineModeEnabled() || !connectivityMonitor.isOnline() || !this._apiService) return;
    await this.fillCalendarWindow().catch(() => {});
    await this.refreshCalendarWindow({ onlyIfStale: true });
  }

  async refreshCalendarWindow({ onlyIfStale = false } = {}) {
    if (!isOfflineModeEnabled() || !connectivityMonitor.isOnline() || !this._apiService) return;
    try {
      const meta = await cacheWriter.getCacheMeta('calendarWindow');
      if (!meta?.start) return;
      if (onlyIfStale && (Date.now() - (meta.lastIncrementalAt || 0)) < REFRESH_MIN_AGE_MS) return;
      const since = new Date((meta.lastIncrementalAt || Date.now()) - 60000).toISOString();
      // The response flows into the cache via the middleware pass-through.
      await this._apiService.getCalendarAppointments({
        start_date: meta.start,
        end_date: meta.end,
        updated_after: since,
      });
      await offlineDb.cacheMeta.put({ ...meta, lastIncrementalAt: Date.now() });
    } catch (e) {
      console.warn('[PrefetchService] incremental refresh failed:', e.message);
    }
  }

  // -------------------------------------------------------------------------
  // Chart depth
  // -------------------------------------------------------------------------

  async _prefetchChartDepth(providerId, apiService) {
    const now = new Date();
    const start = toDateStr(addDays(now, -CHART_PAST_DAYS));
    const end = toDateStr(addBusinessDays(now, CHART_FUTURE_BUSINESS_DAYS));

    // Own appointments only, soonest first.
    const appts = await offlineDb.appointments
      .where('appointment_date')
      .between(start, end, true, true)
      .filter(a => a.provider_id === providerId && CHART_STATUSES.includes(a.status))
      .toArray();

    appts.sort((a, b) => (a.appointment_date || '').localeCompare(b.appointment_date || ''));

    const patientIds = [];
    const seen = new Set();
    for (const a of appts) {
      const pid = a.patient_id || a.patient?.id;
      if (!pid || seen.has(pid)) continue;
      seen.add(pid);
      // Skip charts fetched recently (a reload, a second tab).
      const meta = await cacheWriter.getCacheMeta(`chart:${pid}`);
      if (meta && (Date.now() - (meta.lastFetched || 0)) < CHART_FRESH_MS) continue;
      patientIds.push(pid);
      if (patientIds.length >= MAX_CHART_PATIENTS) break;
    }

    await this._updateState(providerId, 'in_progress', 0, patientIds.length);

    let completed = 0;
    for (let i = 0; i < patientIds.length; i += MAX_CONCURRENT_PATIENTS) {
      if (this._aborted()) break;
      const batch = patientIds.slice(i, i + MAX_CONCURRENT_PATIENTS);
      await Promise.allSettled(batch.map(pid => this._prefetchPatientData(pid, apiService)));
      completed += batch.length;
      await this._updateState(providerId, 'in_progress', completed, patientIds.length);
    }
  }

  async _prefetchPatientData(patientId, apiService) {
    if (this._aborted()) return;

    let patientFetched = false;
    const fetches = [
      this._safe(() => apiService.getPatient(patientId).then(async d => {
        await cacheWriter.cachePatient(d);
        patientFetched = Boolean(d?.id);
      })),
      this._safe(() => apiService.getCases({ patient_id: patientId, status: 'active' }).then(d => cacheWriter.cacheCases(d))),
      this._safe(() => apiService.getPlansOfCare({ patient_id: patientId }).then(d => cacheWriter.cachePlansOfCare(d))),
      this._safe(() => apiService.getClinicalNotes({ patient_id: patientId }).then(d => cacheWriter.cacheClinicalNotes(d))),
      this._safe(() => apiService.getEncounters({ patient_id: patientId }).then(d => cacheWriter.cacheEncounters(d))),
      this._safe(() => apiService.getPatientForms(patientId).then(d => cacheWriter.cacheFormResponses(d))),
      this._safe(() => apiService.getPatientReports(patientId).then(d => cacheWriter.cacheFormReports(d))),
      // Flow sheet entries: prior values + carry-forward source. The
      // response caches via the middleware pass-through as well, but the
      // explicit call guarantees coverage for chart-depth patients.
      this._safe(() => apiService.getFlowSheetEntries({ patient_id: patientId, is_active: true, limit: 500 })),
    ];

    await Promise.allSettled(fetches);

    // Stamp only a real fetch, so a run that failed or was cut short retries.
    if (patientFetched && !this._aborted()) {
      await cacheWriter.updateCacheMeta(`chart:${patientId}`, {});
    }
  }

  // -------------------------------------------------------------------------
  // Org-level data
  // -------------------------------------------------------------------------

  async _prefetchOrgData(organizationId, userId, apiService) {
    const orgMeta = await cacheWriter.getCacheMeta(`org:${organizationId}`);
    if (orgMeta && (Date.now() - orgMeta.lastFetched) < ORG_STALE_MS) {
      return;
    }

    let scheduleUserIds = null;

    await Promise.allSettled([
      this._safe(async () => {
        const users = await apiService.getOrganizationUsers(organizationId);
        await cacheWriter.cacheOrganizationUsers(users);
        const list = Array.isArray(users) ? users : users?.items || [];
        scheduleUserIds = list.filter(u => u.is_provider).map(u => u.id);
      }),
      this._safe(() => apiService.getAppointmentTypes(organizationId).then(d => cacheWriter.cacheAppointmentTypes(d))),
      this._safe(() => apiService.getBillingRules({ organization_id: organizationId, is_active: true }).then(d => cacheWriter.cacheBillingRules(d))),
      this._safe(() => apiService.getInterventionCatalog({ is_active: true, limit: 500 }).then(d => cacheWriter.cacheInterventionCatalog(d))),
      // Cached via middleware pass-through (organizations / publicCache /
      // flowSheetCatalog); issuing the request is the write.
      this._safe(() => apiService.getOrganization(organizationId)),
      this._safe(() => apiService.getLocations(organizationId)),
      this._safe(() => apiService.getFlowSheetCatalogItems({ is_active: true, organization_id: organizationId, limit: 200 })),
      this._safe(async () => {
        const templates = await apiService.getNoteTemplates(organizationId, userId);
        const items = Array.isArray(templates) ? templates : templates?.items || [];
        await cacheWriter.cacheTemplates(items);
        for (let i = 0; i < items.length; i += 3) {
          if (this._aborted()) break;
          const batch = items.slice(i, i + 3);
          await Promise.allSettled(
            batch.map(t => this._safe(() =>
              apiService.getTemplate(t.id).then(full => cacheWriter.cacheTemplate(full))
            ))
          );
        }
      }),
    ]);

    // Warm the availability/overrides keys the calendar grid requests
    // (exact-endpoint publicCache; the store asks with the full provider csv).
    if (scheduleUserIds?.length) {
      const idsCsv = scheduleUserIds.join(',');
      await Promise.allSettled([
        this._safe(() => apiService.getProviderAvailabilityPublic(idsCsv, { organizationId })),
        this._safe(() => apiService.getScheduleOverridesPublic(idsCsv, undefined, undefined, { organizationId })),
      ]);
    }

    await cacheWriter.updateCacheMeta(`org:${organizationId}`, { organizationId });
  }

  async _safe(fn) {
    try {
      return await fn();
    } catch (e) {
      if (e.name === 'AbortError') throw e;
      console.warn('[PrefetchService] Non-critical fetch failed:', e.message);
      return null;
    }
  }

  async _updateState(providerId, status, completed, total) {
    await offlineDb.prefetchState.put({
      providerId,
      status,
      completed,
      total,
      lastUpdated: Date.now(),
    });
  }
}

export const prefetchService = new PrefetchService();

// Org setting turned off remotely (see featureFlag.js): stop at once.
onOfflineModeDisabled(() => prefetchService.abort());
